import {FC, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '../auth'
import {AuthAuroraShell, AuthBrandHeader} from '../auth/components/AuthAurora'
import {finishOnboarding, setOnboardingDismissed} from './core/_requests'

// Vídeo de apresentação do produto (overview mais recente, confirmado em 2026-09-20).
const WELCOME_VIDEO_URL = 'https://www.youtube.com/embed/2Rr03-TBxB8?rel=0'

// Boas-vindas do primeiro acesso: uma tela só, sem cadastro. O resto vive no checklist do dashboard.
// Substitui o wizard de 5 a 6 passos (stepper do Metronic), que replicava telas que já existem.
export const WelcomePage: FC = () => {
  const {currentUser, setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const [showVideo, setShowVideo] = useState(false)
  const [loading, setLoading] = useState<'start' | 'explore' | null>(null)
  const [error, setError] = useState('')

  const firstName = (currentUser?.name ?? '').trim().split(' ')[0]
  const isManagerLike = !!currentUser?.administrator || !!currentUser?.manager

  // "Começar" leva ao dashboard com o checklist. "Explorar sozinho" também, mas com o checklist
  // oculto (ele volta em Ajuda > Primeiros passos).
  const enter = async (mode: 'start' | 'explore') => {
    setLoading(mode)
    setError('')

    try {
      await finishOnboarding()
      if (mode === 'explore') await setOnboardingDismissed(true)
      setCurrentUser({...currentUser!, onboarded: 1})
      navigate('/dashboard', {replace: true})
    } catch (e) {
      console.error(e)
      setError('Não foi possível continuar agora. Tente novamente em instantes.')
      setLoading(null)
    }
  }

  return (
    <AuthAuroraShell>
      <AuthBrandHeader />

      <div className='text-center mb-8'>
        <h1 className='text-dark fw-bolder mb-3'>Olá, {firstName}!</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {isManagerLike
            ? 'Em poucos minutos você terá o primeiro projeto rodando.'
            : 'Em poucos minutos você lança a sua primeira hora.'}{' '}
          Vamos por partes, no seu ritmo.
        </div>
      </div>

      {showVideo ? (
        <div className='ratio ratio-16x9 mb-8 onboarding-welcome-video'>
          <iframe
            src={WELCOME_VIDEO_URL}
            title='Apresentação do TaskRush'
            allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
            referrerPolicy='strict-origin-when-cross-origin'
            allowFullScreen
          />
        </div>
      ) : (
        <div className='text-center mb-8'>
          <button
            type='button'
            className='btn btn-link link-primary fw-semibold'
            onClick={() => setShowVideo(true)}
          >
            <i className='bi bi-play-circle me-2'></i>
            Assistir à apresentação do TaskRush
          </button>
        </div>
      )}

      {error && (
        <div className='mb-8 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{error}</div>
        </div>
      )}

      <div className='d-grid gap-3'>
        <button
          type='button'
          className='btn btn-primary'
          onClick={() => enter('start')}
          disabled={loading !== null}
        >
          <span className='indicator-label'>Começar</span>
          {loading === 'start' && (
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          )}
        </button>
        <button
          type='button'
          className='btn btn-light'
          onClick={() => enter('explore')}
          disabled={loading !== null}
        >
          <span className='indicator-label'>Explorar sozinho</span>
          {loading === 'explore' && (
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          )}
        </button>
      </div>

      <div className='text-gray-500 fs-7 text-center mt-6'>
        Os primeiros passos ficam sempre disponíveis em Ajuda.
      </div>
    </AuthAuroraShell>
  )
}
